import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Heart, CheckCircle2, ClipboardCheck, Lock, BookOpen, Brain, Headphones, Scale, Puzzle, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAccessControl } from '../hooks/useAccessControl';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GuideAccordion = ({ title, icon: Icon, defaultOpen, children }) => {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div className="border border-sage-200 rounded-xl overflow-hidden bg-white">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 p-4 text-left">
        <Icon className="w-5 h-5 text-sage-400 flex-shrink-0" />
        <span className="text-sm font-medium text-sage-700 flex-1">{title}</span>
        <ChevronDown className={`w-4 h-4 text-sage-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
};

export const Methode = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { isTrial, canAccessChapter } = useAccessControl();
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChapters();
  }, []);

  const fetchChapters = async () => {
    try {
      const response = await axios.get(`${API_URL}/chapters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChapters(response.data);
    } catch (error) {
      console.error('Erreur chargement chapitres:', error);
      toast.error('Erreur lors du chargement des chapitres');
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (chapterId) => {
    try {
      await axios.post(`${API_URL}/chapters/${chapterId}/favorite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh
      fetchChapters();
    } catch (error) {
      console.error('Erreur toggle favori:', error);
    }
  };

  const filteredChapters = chapters;

  const favorites = filteredChapters.filter(c => c.is_favorite);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-700 font-sans">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="methode-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-sage-100 to-sand-50 px-6 py-8">
        <h1 className="text-4xl font-serif font-light text-sage-700 mb-2" data-testid="methode-title">
          Méthode
        </h1>
        <p className="text-sage-700/70 font-sans mb-2">
          10 déclics pour comprendre et appliquer la méthode
        </p>
        
        {/* Indicateur de progression */}
        {chapters.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-sage-600">Votre progression</span>
              <span className="text-sage-700 font-medium">
                {chapters.filter(c => c.is_read).length}/{chapters.length} lus
              </span>
            </div>
            <div className="w-full h-2 bg-sage-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-declic rounded-full transition-all duration-500"
                style={{ width: `${(chapters.filter(c => c.is_read).length / chapters.length) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* ====== GUIDE PRATIQUE — À lire en premier ====== */}
      <div className="px-6 py-4">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="w-5 h-5 text-declic" />
          <h2 className="text-lg font-sans font-medium text-sage-700">Guide pratique</h2>
          <span className="bg-declic/10 text-declic text-xs font-medium px-2 py-0.5 rounded-full">À lire en premier</span>
        </div>

        <div className="space-y-2">
          <GuideAccordion title="L'inconscient : votre enfant intérieur" icon={Brain} defaultOpen={true}>
            <div className="markdown-content prose-sage text-sm">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
{`L'inconscient est souvent comparé à un **enfant intérieur** – et ce n'est pas une simple métaphore.

De nombreux spécialistes en neurosciences et en thérapie brève s'accordent à dire que l'inconscient fonctionne avec une logique émotionnelle, imagée et intuitive, proche de celle d'un enfant de 7 ans environ.

> **En résumé** : votre conscient a l'âge de votre corps, votre inconscient a 7 ans d'âge mental.

Cela signifie qu'il ne raisonne pas comme un adulte rationnel. Il comprend mieux les **histoires, les métaphores, les émotions et les images mentales** que les discours analytiques.

C'est pourquoi les séances d'hypnose utilisent un langage simple, sensoriel et symbolique. En hypnose, on s'adresse à cette partie avec **douceur, bienveillance et confiance** – comme on parlerait à un enfant qu'on guide avec patience.`}
              </ReactMarkdown>
            </div>
          </GuideAccordion>

          <GuideAccordion title="Ce que gère votre inconscient" icon={Brain}>
            <div className="markdown-content prose-sage text-sm">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
{`### Les émotions
Peur, joie, tristesse, colère, dégoût... Toutes les réactions émotionnelles sont traitées de façon automatique.

### Les automatismes
Lire, écrire, conduire, faire du vélo, marcher sans y penser... Tous ces gestes appris deviennent inconscients.

### Les habitudes et conditionnements
Votre rythme de vie, vos comportements alimentaires, vos rituels sont gérés par l'inconscient.

### Le fonctionnement corporel
L'inconscient régule la respiration, la digestion, le rythme cardiaque, les sécrétions hormonales. Il participe également à la gestion du poids, de la faim et de la satiété.

### Le sommeil
L'endormissement, les cycles de sommeil et les rêves sont des processus automatiques.

### La mémoire
Votre inconscient stocke et organise les souvenirs, y compris les mémoires "implicites" et les souvenirs émotionnels.

### La protection et la survie
Face au danger, l'inconscient déclenche des réflexes immédiats de protection.

### La créativité et l'intuition
L'inconscient est le berceau de la créativité, des idées spontanées et des intuitions.

> **En résumé** : l'inconscient est le grand régisseur silencieux de votre corps, de vos réactions et de vos comportements. La conscience gère 5 à 10 % de notre fonctionnement, et l'inconscient... tout le reste.`}
              </ReactMarkdown>
            </div>
          </GuideAccordion>

          <GuideAccordion title="Comment écouter vos séances" icon={Headphones}>
            <div className="markdown-content prose-sage text-sm">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
{`### La méthode recommandée

1. **Alternez les versions** : Pour chaque thème, écoutez une fois la version **Zen** (en position assise) et une fois la version **Mouvement** (en marchant).

2. **Espacez les écoutes** : Vous pouvez espacer les deux versions de quelques jours selon votre ressenti.

3. **Accordez-vous une pause** : Après les deux versions d'un même thème, laissez **un jour de pause** avant de passer au suivant.

---

### Le bon environnement
- Lieu calme, sans bruits de fond
- Téléphone en mode avion
- Prévenez votre entourage

### Le bon matériel
- **Casque ou écouteurs** de bonne qualité pour une immersion totale
- Évitez l'écoute sur haut-parleur

### La bonne position
- **Assis confortablement**, dos soutenu, pieds au sol
- Évitez la position allongée (risque d'endormissement)

### Le point de fixation
- Fixez un petit point sur votre main ou face à vous
- Cela canalise l'attention et prépare le cerveau

### Laisser faire l'expérience
- Ne cherchez pas à "réussir"
- Acceptez les pensées passagères
- Faites confiance à votre inconscient

### Visualiser = Imaginer = Penser
Vous n'avez pas besoin de "bien voir". Imaginer ou simplement penser à quelque chose est totalement efficace pour votre inconscient.

### Après la séance
- Bougez les doigts et les orteils
- Respirez profondément
- Étirez-vous légèrement
- **Buvez un peu d'eau**`}
              </ReactMarkdown>
            </div>
          </GuideAccordion>

          <GuideAccordion title="L'importance de la régularité" icon={Scale}>
            <div className="markdown-content prose-sage text-sm">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
{`### « Je n'ai pas le temps »

C'est une phrase courante. Et pourtant, il ne s'agit souvent pas d'un manque de temps... mais de ce à quoi vous décidez de consacrer votre énergie.

**30 minutes par jour suffisent pour changer une vie** – si on décide de les accorder à SOI.

---

### La régularité crée le changement

Une séance ponctuelle peut être bénéfique, mais c'est la **régularité** qui crée le changement durable.

- Écoutez vos séances plusieurs fois par semaine
- Plus vous pratiquez, plus votre inconscient s'imprègne des suggestions
- Chaque écoute renforce les nouveaux apprentissages

> **Faites confiance à votre inconscient**, il sait comment intégrer ces nouvelles habitudes de manière fluide et naturelle.`}
              </ReactMarkdown>
            </div>
          </GuideAccordion>

          <GuideAccordion title="Méthode + Audios = Le duo gagnant" icon={Puzzle}>
            <div className="markdown-content prose-sage text-sm">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
{`### Pourquoi les deux ?

**La Méthode** (ce que vous lisez) s'adresse à votre **cerveau conscient**, intelligent et rationnel.

Elle vous donne :
- Les clés pour **comprendre** comment fonctionne votre corps
- Les outils pour **éduquer** correctement votre inconscient
- La logique derrière chaque conseil

---

**Les Audios** s'adressent directement à votre **inconscient**.

Ils permettent de :
- **Apprivoiser** cette partie de vous qui gère 90% de vos comportements
- **Modifier** en douceur vos goûts, envies et habitudes
- **Aligner** votre inconscient avec vos objectifs conscients

---

### L'objectif commun

Votre **conscient** et votre **inconscient** doivent travailler ensemble vers le même but : **perdre du poids, sereinement**.

> La Méthode informe votre conscient. Les Audios transforment votre inconscient. **Ensemble, ils créent le changement durable.**`}
              </ReactMarkdown>
            </div>
          </GuideAccordion>
        </div>

        <div className="h-px bg-sage-200 my-6" />
        <p className="text-sage-400 text-xs text-center">Puis continuez avec les 10 déclics ci-dessous</p>
      </div>

      {/* Favoris */}
      {favorites.length > 0 && (
        <div className="px-6 py-4">
          <h2 className="text-lg font-sans font-medium text-sage-700 mb-3 flex items-center">
            <Heart className="w-5 h-5 mr-2 text-declic fill-declic" />
            Favoris
          </h2>
          <div className="space-y-2">
            {favorites.map((chapter) => (
              <ChapterCard
                key={chapter.id}
                chapter={chapter}
                onToggleFavorite={toggleFavorite}
                onClick={() => navigate(`/methode/${chapter.slug}`)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Tous les chapitres */}
      <div className="px-6 py-4">
        {favorites.length > 0 && (
          <h2 className="text-lg font-sans font-medium text-sage-700 mb-3">
            Tous les chapitres
          </h2>
        )}
        <div className="space-y-2">
          {filteredChapters.map((chapter) => (
            <ChapterCard
              key={chapter.id}
              chapter={chapter}
              locked={!canAccessChapter(chapter.id)}
              onToggleFavorite={toggleFavorite}
              onClick={() => canAccessChapter(chapter.id) ? navigate(`/methode/${chapter.slug}`) : toast.error('Passez à l\'accès complet pour débloquer ce déclic')}
            />
          ))}
        </div>

        {/* Lien vers le quiz */}
        {(
          <div
            className="mt-6 bg-gradient-to-r from-sage-500 to-sage-600 rounded-xl p-5 cursor-pointer hover:from-sage-600 hover:to-sage-900 transition-all"
            onClick={() => navigate('/quiz')}
            data-testid="quiz-link"
          >
            <div className="flex items-center gap-3 text-white">
              <ClipboardCheck className="w-8 h-8 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-base">Quiz : avez-vous bien compris ?</h3>
                <p className="text-white/70 text-sm">Testez vos connaissances sur la méthode</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ChapterCard = ({ chapter, locked, onToggleFavorite, onClick }) => {
  return (
    <div
      className={`bg-white rounded-xl border p-4 cursor-pointer transition-colors ${
        locked
          ? 'border-sage-200 opacity-60'
          : chapter.is_read ? 'border-sage-300 bg-sage-50/50 hover:border-sage-500' : 'border-sand-200 hover:border-sage-500'
      }`}
      onClick={onClick}
      data-testid={`chapter-card-${chapter.slug}`}
    >
      <div className="flex items-start gap-3">
        {/* Indicateur Lu */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
          locked ? 'bg-sage-200' : chapter.is_read ? 'bg-sage-500' : 'bg-sage-100'
        }`}>
          {locked ? (
            <Lock className="w-4 h-4 text-sage-400" />
          ) : chapter.is_read ? (
            <CheckCircle2 className="w-5 h-5 text-white" />
          ) : (
            <span className="text-sm font-medium text-sage-400">{chapter.index}</span>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <h3 className="font-sans font-medium text-sage-700 text-sm leading-tight">
                {chapter.title.replace(/^\d+\)\s*/, '')}
              </h3>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="flex-shrink-0 -mt-1"
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite(chapter.id);
              }}
              data-testid={`chapter-favorite-${chapter.slug}`}
            >
              <Heart
                className={`w-5 h-5 ${
                  chapter.is_favorite
                    ? 'text-declic fill-declic'
                    : 'text-sage-700/40'
                }`}
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Methode;